import { useCallback, useEffect, useState } from 'react';
import { IconButton } from '@material-ui/core';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import ChevronRight from '@material-ui/icons/ChevronRight';
import { useHomeStyles } from './homeStyles';

type Edges = { start: boolean; end: boolean };

/**
 * Where the track should scroll to move one card in `direction`, given each
 * card's offset from the first. The first card at or past the left edge is
 * the current one, so a half-scrolled track counts the card coming into view;
 * the result is clamped to the first and last cards.
 */
export function nextCardOffset(
  offsets: number[],
  scrollLeft: number,
  direction: 1 | -1,
): number | undefined {
  if (!offsets.length) return undefined;
  let current = offsets.findIndex(x => x >= scrollLeft - 4);
  if (current < 0) current = offsets.length - 1;
  return offsets[
    Math.min(offsets.length - 1, Math.max(0, current + direction))
  ];
}

/**
 * A sideways card track that snaps card by card. Returns a ref for the track,
 * whether it is at either end, and a step that moves it one card along. The
 * ends follow the track however it moves: the buttons, a trackpad, a swipe.
 * The ref is a callback, so a track that mounts later (after content loads,
 * or keyed per tab) is picked up without extra wiring.
 */
export function useScrollTrack() {
  const [track, setTrack] = useState<HTMLElement | null>(null);
  const [edges, setEdges] = useState<Edges>({ start: true, end: true });
  const measure = useCallback(() => {
    if (!track) return;
    const max = track.scrollWidth - track.clientWidth;
    const start = track.scrollLeft <= 2;
    const end = track.scrollLeft >= max - 2;
    setEdges(prev =>
      prev.start === start && prev.end === end ? prev : { start, end },
    );
  }, [track]);
  useEffect(() => {
    if (!track) return undefined;
    measure();
    const observer =
      typeof ResizeObserver === 'undefined'
        ? undefined
        : new ResizeObserver(measure);
    observer?.observe(track);
    track.addEventListener('scroll', measure, { passive: true });
    return () => {
      observer?.disconnect();
      track.removeEventListener('scroll', measure);
    };
  }, [track, measure]);
  const step = (direction: 1 | -1) => {
    if (!track) return;
    const cards = Array.from(track.children) as HTMLElement[];
    const offsets = cards.map(
      card => card.offsetLeft - (cards[0]?.offsetLeft ?? 0),
    );
    const left = nextCardOffset(offsets, track.scrollLeft, direction);
    if (left === undefined) return;
    track.scrollTo({
      left,
      behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
        ? 'auto'
        : 'smooth',
    });
  };
  return { ref: setTrack, edges, step };
}

/** The label and previous / next buttons that sit above a track. */
export function ScrollTrackControls({
  label,
  noun,
  trackId,
  edges,
  step,
}: {
  label: string;
  /** What the buttons move through, e.g. "articles". */
  noun: string;
  trackId: string;
  edges: Edges;
  step: (direction: 1 | -1) => void;
}) {
  const classes = useHomeStyles();
  return (
    <div className={classes.scrollControls}>
      <span>{label}</span>
      {/* aria-disabled, not disabled: a disabled button drops keyboard focus
          to the page when the track reaches its end. */}
      <IconButton
        aria-label={`Previous ${noun}`}
        aria-controls={trackId}
        aria-disabled={edges.start}
        onClick={() => !edges.start && step(-1)}
      >
        <ChevronLeft />
      </IconButton>
      <IconButton
        aria-label={`Next ${noun}`}
        aria-controls={trackId}
        aria-disabled={edges.end}
        onClick={() => !edges.end && step(1)}
      >
        <ChevronRight />
      </IconButton>
    </div>
  );
}
